import type { AddinSettings } from "./settings";
import {
  getGraphTokenSilent,
  getGraphTokenInteractive,
  getActiveGraphAccountName,
  disconnectGraph,
  ensureGraphToken,
  isNaaSupported,
  BASELINE_SCOPES,
  FEATURE_SCOPES,
  GraphConsentRequiredError,
} from "./office-auth";
import { GRAPH_CLIENT_ID } from "./config";
import { debugLog } from "./debug";

// User-facing message when mailbox-wide access needs an account connection.
const CONNECT_ACCOUNT_MESSAGE =
  "Connect your account to search your mailbox. " +
  "Summarizing, drafting, and replying to the currently open email still work.";

// Shared by every adapter: compose/reply always goes through Office.js
// (ADR-001), regardless of whether the adapter reads mail via EWS or Graph.
// Centralizing here means the `<br>` line-break conversion is fixed in one
// place instead of drifting across OfficeJsAdapter / GraphAdapter /
// UnavailableMailboxAdapter.
export function openComposeWindow(to: string, subject: string, body: string): void {
  Office.context.mailbox.displayNewMessageForm({
    toRecipients: [to],
    subject,
    htmlBody: `<p>${body.replace(/\n/g, "<br>")}</p>`,
  });
}

export function openReplyWindow(body: string): void {
  const item = Office.context.mailbox.item;
  if (!item) throw new Error("No active mail item");
  item.displayReplyForm({ htmlBody: `<p>${body.replace(/\n/g, "<br>")}</p>` });
}

/**
 * Opens an existing email (by id) in a separate Outlook reading window, so a
 * subject shown in chat can be clicked open without disturbing the mailbox's
 * own reading-pane selection (Office.js has no API to drive that selection
 * from a task pane). `displayMessageForm` requires an EWS-formatted id: the
 * EWS adapter's ids are already in that format, but Graph/REST ids must be
 * converted first via `convertToEwsId`.
 */
export function openEmailForm(itemId: string, adapterType: MailboxAdapter["type"]): void {
  const ewsId =
    adapterType === "graph"
      ? Office.context.mailbox.convertToEwsId(itemId, Office.MailboxEnums.RestVersion.v2_0)
      : itemId;
  Office.context.mailbox.displayMessageForm(ewsId);
}

export interface EmailSummary {
  id: string;
  subject: string;
  from: string;
  receivedAt: string;
  isRead: boolean;
  preview: string;
  categories: string[];
}

export interface SearchOptions {
  query?: string;
  dateFrom?: string;
  dateTo?: string;
  from?: string;
}

export type ListFilter = "today" | "unread" | "needs_reply" | "recent";

export interface CalendarEvent {
  id: string;
  subject: string;
  start: string;
  end: string;
  organizer: string;
  location: string;
}

export interface MailFolder {
  id: string;
  displayName: string;
}

/** An Outlook category definition (a "label") from `masterCategories`. */
export interface MasterCategory {
  id: string;
  displayName: string;
  /** Preset colour name, e.g. `preset0`. `none` when no colour is assigned. */
  color: string;
}

/** A Microsoft Purview retention label definition (org-level). */
export interface RetentionLabel {
  id: string;
  displayName: string;
}

export interface MailboxAdapter {
  readonly type: "graph" | "officejs" | "unavailable";
  listEmails(filter: ListFilter, folder?: string): Promise<EmailSummary[]>;
  searchEmails(options: SearchOptions): Promise<EmailSummary[]>;
  getEmailBody(itemId: string): Promise<string>;
  /**
   * Fetches a single message's summary (subject, from, etc.) by ID — used to
   * resolve a human-readable subject for the delete-email confirmation dialog
   * (M3) rather than showing the opaque item ID.
   */
  getEmailSummary(itemId: string): Promise<EmailSummary>;
  draftNewEmail(to: string, subject: string, body: string): Promise<void>;
  replyToEmail(itemId: string, body: string): Promise<void>;
  listCalendarEvents(fromIso: string, toIso: string): Promise<CalendarEvent[]>;
  /**
   * Creates a new event on the signed-in user's calendar. NON-destructive —
   * creating your own event is easily undone (delete/edit in Outlook), so
   * `agent-loop.ts` never asks for confirmation before calling this. Returns
   * the created event's id.
   */
  createEvent(event: {
    subject: string;
    start: string;
    end: string;
    attendees?: string[];
    body?: string;
  }): Promise<string>;
  /**
   * Responds to a meeting invite (accept / decline / tentatively accept) on
   * behalf of the signed-in user. Unlike {@link createEvent}, this is a
   * one-way signal visible to the organizer — `agent-loop.ts` requires the
   * user to confirm before invoking this.
   */
  respondToEvent(
    eventId: string,
    response: "accept" | "decline" | "tentativelyAccept"
  ): Promise<void>;
  listFolders(): Promise<MailFolder[]>;
  moveEmail(itemId: string, folderId: string): Promise<void>;
  applyCategories(itemId: string, categories: string[]): Promise<void>;
  createFolder(name: string, parentFolderId?: string): Promise<MailFolder>;
  deleteFolder(folderId: string): Promise<void>;
  deleteEmail(messageId: string): Promise<void>;
  // Labels (== Outlook categories, managed via masterCategories). All
  // non-destructive; none prompt for confirmation.
  listLabels(): Promise<MasterCategory[]>;
  createLabel(name: string, color?: string): Promise<MasterCategory>;
  applyLabel(messageId: string, names: string[]): Promise<void>;
  removeLabel(messageId: string, names: string[]): Promise<void>;
  // Microsoft Purview retention labels. Read the org's label definitions and
  // (best-effort) assign one to a message. Frequently unavailable per tenant —
  // callers must degrade gracefully. See GraphAdapter for the API details.
  listRetentionLabels(): Promise<RetentionLabel[]>;
  applyRetentionLabel(messageId: string, label: string): Promise<void>;
  /**
   * Reads the Microsoft Purview sensitivity label (MIP/AIP) display name applied
   * to a message, used by the AI-egress classification gate (ADR-005). Returns
   * `null` whenever the label can't be determined — no label applied, the
   * adapter has no way to read it (EWS/Office.js), or the read fails — never
   * throws, since a read failure here must degrade to "unknown" classification
   * rather than break the caller.
   */
  getSensitivityLabel(messageId: string): Promise<string | null>;
  /**
   * One-way sync of a local follow-up task to Microsoft To Do (dedicated "AI
   * Assistant" list, created lazily). Graph-only — EWS/unavailable adapters
   * throw a clear "requires Graph" error so the caller can degrade to
   * local-only rather than silently doing nothing. Returns the new To Do
   * task id.
   */
  createRemoteTask(title: string, dueDate?: string): Promise<string>;
  /**
   * Overwrites title/due date/completion on an already-synced remote task
   * with the full current local state (always a full write, never a partial
   * diff) — `dueDate` absent means "clear the due date", not "leave
   * unchanged", so the caller must always pass the task's current full state.
   */
  updateRemoteTask(
    taskId: string,
    task: { title: string; dueDate?: string; done: boolean }
  ): Promise<void>;
  deleteRemoteTask(taskId: string): Promise<void>;
}

// Read-only calendar grounding requires Graph (Exchange Online). EWS on-premises
// mailboxes have no calendarView-equivalent wired up in v1 — degrade cleanly
// with a clear, actionable message rather than a confusing protocol error.
const CALENDAR_REQUIRES_GRAPH_MESSAGE = "Calendar requires Graph (Exchange Online).";

// Calendar writes (create event, respond to meeting) are also Graph-only in
// v1 — EWS has no equivalent wired up, so degrade with the same clear message
// pattern as everything else.
const CALENDAR_WRITE_REQUIRES_GRAPH_MESSAGE = "Calendar actions require Graph (Exchange Online).";

// Listing folders and moving messages between them are Graph-only in v1 (see
// plan gap-4) — EWS move/folder-listing is not implemented.
const FOLDERS_REQUIRE_GRAPH_MESSAGE = "Listing folders requires Graph (Exchange Online).";
const MOVE_REQUIRES_GRAPH_MESSAGE = "Moving email requires Graph (Exchange Online).";

// Creating/deleting folders and deleting messages are also Graph-only in v1 —
// EWS has no equivalent wired up, so degrade with the same clear message
// pattern rather than a confusing protocol error.
const CREATE_FOLDER_REQUIRES_GRAPH_MESSAGE = "Creating folders requires Graph (Exchange Online).";
const DELETE_FOLDER_REQUIRES_GRAPH_MESSAGE = "Deleting folders requires Graph (Exchange Online).";
const DELETE_EMAIL_REQUIRES_GRAPH_MESSAGE = "Deleting email requires Graph (Exchange Online).";

// Microsoft To Do sync is Graph-only in v1 — EWS has no equivalent task API
// wired up, so degrade with the same clear message pattern as everything else.
const TASK_SYNC_REQUIRES_GRAPH_MESSAGE = "Task sync requires Graph (Exchange Online).";

// Office.js can only apply categories to the currently open mail item — it has
// no API to write categories on an arbitrary message by ID.
const APPLY_CATEGORIES_CURRENT_ITEM_ONLY_MESSAGE =
  "Applying categories without Graph is only supported for the currently open email.";

// Label management (masterCategories) and retention labels are Graph-only in
// v1 — EWS has no equivalent wired up, so degrade with a clear message.
const LABELS_REQUIRE_GRAPH_MESSAGE = "Managing labels requires Graph (Exchange Online).";
const RETENTION_REQUIRES_GRAPH_MESSAGE =
  "Managing retention labels requires Graph (Exchange Online).";

// Surfaced when the Purview retention API or the per-item assignment isn't
// usable for this account/tenant (permission not consented is handled
// separately via GraphConsentRequiredError; this covers 403/404/not-supported
// and the fact that there is no first-class delegated Graph endpoint to assign
// a retention label to an individual mail item — see applyRetentionLabel).
const RETENTION_UNAVAILABLE_MESSAGE =
  "Retention labels aren't available for this account or tenant.";

// Wraps a lower-level error as a user-readable "not available" error, preserving
// the original as `cause` (A3) without relying on the Error(message, {cause})
// constructor overload, which the project's TS lib target doesn't expose.
function retentionUnavailable(cause: unknown): Error {
  const error = new Error(RETENTION_UNAVAILABLE_MESSAGE);
  (error as Error & { cause?: unknown }).cause = cause;
  return error;
}

/**
 * Applies categories via Office.js `item.categories.addAsync`, which only
 * operates on the currently open mail item. Shared by `OfficeJsAdapter` and
 * `UnavailableMailboxAdapter` since neither has Graph write access.
 */
function applyCategoriesToCurrentItem(itemId: string, categories: string[]): Promise<void> {
  const item = Office.context.mailbox.item;
  if (!item) throw new Error("No active mail item");
  const currentItemId = (item as { itemId?: string }).itemId;
  if (currentItemId && currentItemId !== itemId) {
    throw new Error(APPLY_CATEGORIES_CURRENT_ITEM_ONLY_MESSAGE);
  }
  return new Promise((resolve, reject) => {
    item.categories.addAsync(categories, (result: Office.AsyncResult<void>) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(`Failed to apply categories: ${result.error?.message ?? "unknown"}`));
        return;
      }
      resolve();
    });
  });
}

// ─── EWS helpers ────────────────────────────────────────────────────────────

const EWS_NS = {
  SOAP: "http://schemas.xmlsoap.org/soap/envelope/",
  T: "http://schemas.microsoft.com/exchange/services/2006/types",
  M: "http://schemas.microsoft.com/exchange/services/2006/messages",
};

function isIsoDate(value: string | undefined): value is string {
  return !!value && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

// OData string literals: escape ' by doubling it, per OData V4 spec.
function escapeODataLiteral(value: string): string {
  return value.replace(/'/g, "''");
}

// OData $search phrase: also strip " since it terminates the quoted phrase.
function escapeODataQuoted(value: string): string {
  return escapeODataLiteral(value).replace(/"/g, "");
}

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function ewsEnvelope(bodyContent: string): string {
  return `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:soap="${EWS_NS.SOAP}" xmlns:t="${EWS_NS.T}" xmlns:m="${EWS_NS.M}">
    <soap:Header><t:RequestServerVersion Version="Exchange2013"/></soap:Header>
    <soap:Body>${bodyContent}</soap:Body>
</soap:Envelope>`;
}

function tag(el: Document | Element, localName: string): Element | undefined {
  return el.getElementsByTagNameNS(EWS_NS.T, localName)[0];
}

function parseEwsItems(doc: Document): EmailSummary[] {
  const msgs = doc.getElementsByTagNameNS(EWS_NS.T, "Message");
  const results: EmailSummary[] = [];
  for (let i = 0; i < msgs.length; i++) {
    const msg = msgs[i];
    const id = tag(msg, "ItemId")?.getAttribute("Id") ?? "";
    if (!id) continue;
    const subject = tag(msg, "Subject")?.textContent ?? "(no subject)";
    const from = tag(msg, "EmailAddress")?.textContent ?? "";
    const receivedAt = tag(msg, "DateTimeReceived")?.textContent ?? "";
    const isRead = tag(msg, "IsRead")?.textContent?.toLowerCase() === "true";
    const bodyText = tag(msg, "Body")?.textContent ?? "";
    const categoriesEl = tag(msg, "Categories");
    const categories = categoriesEl
      ? Array.from(categoriesEl.getElementsByTagNameNS(EWS_NS.T, "String"))
          .map((s) => s.textContent ?? "")
          .filter((c) => c.length > 0)
      : [];
    results.push({
      id,
      subject,
      from,
      receivedAt,
      isRead,
      preview: bodyText.slice(0, 200),
      categories,
    });
  }
  return results;
}

// ─── OfficeJsAdapter ─────────────────────────────────────────────────────────

export class OfficeJsAdapter implements MailboxAdapter {
  readonly type = "officejs" as const;

  private makeEwsRequest(xml: string): Promise<Document> {
    // Log size only — the SOAP request/response bodies contain email subjects,
    // addresses, and message content (PII); never emit them, even opt-in.
    debugLog("ews:request", { bytes: xml.length });
    return new Promise((resolve, reject) => {
      Office.context.mailbox.makeEwsRequestAsync(xml, (result: Office.AsyncResult<string>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          debugLog("ews:failed", { code: result.error?.code });
          reject(new Error(result.error?.message ?? "EWS request failed"));
          return;
        }
        debugLog("ews:response", { bytes: result.value?.length ?? 0 });
        resolve(new DOMParser().parseFromString(result.value, "text/xml"));
      });
    });
  }

  private findItemXml(restriction: string, queryString?: string): string {
    const restrictionBlock = queryString
      ? `<m:QueryString>${queryString}</m:QueryString>`
      : restriction
        ? `<m:Restriction>${restriction}</m:Restriction>`
        : "";
    return ewsEnvelope(`
        <m:FindItem Traversal="Shallow">
            <m:ItemShape>
                <t:BaseShape>IdOnly</t:BaseShape>
                <t:AdditionalProperties>
                    <t:FieldURI FieldURI="item:Subject"/>
                    <t:FieldURI FieldURI="message:From"/>
                    <t:FieldURI FieldURI="item:DateTimeReceived"/>
                    <t:FieldURI FieldURI="message:IsRead"/>
                    <t:FieldURI FieldURI="item:Body"/>
                    <t:FieldURI FieldURI="item:Categories"/>
                </t:AdditionalProperties>
                <t:BodyType>Text</t:BodyType>
            </m:ItemShape>
            <m:IndexedPageItemView MaxEntriesReturned="50" Offset="0" BasePoint="Beginning"/>
            ${restrictionBlock}
            <m:SortOrder>
                <t:FieldOrder Order="Descending">
                    <t:FieldURI FieldURI="item:DateTimeReceived"/>
                </t:FieldOrder>
            </m:SortOrder>
            <m:ParentFolderIds>
                <t:DistinguishedFolderId Id="inbox"/>
            </m:ParentFolderIds>
        </m:FindItem>`);
  }

  async listEmails(filter: ListFilter): Promise<EmailSummary[]> {
    const today = new Date().toISOString().split("T")[0];
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

    let restriction = "";
    if (filter === "today") {
      restriction = `
            <t:IsGreaterThanOrEqualTo>
                <t:FieldURI FieldURI="item:DateTimeReceived"/>
                <t:FieldURIOrConstant><t:Constant Value="${today}T00:00:00Z"/></t:FieldURIOrConstant>
            </t:IsGreaterThanOrEqualTo>`;
    } else if (filter === "unread" || filter === "needs_reply") {
      const dateFilter = `
            <t:IsGreaterThanOrEqualTo>
                <t:FieldURI FieldURI="item:DateTimeReceived"/>
                <t:FieldURIOrConstant><t:Constant Value="${sevenDaysAgo}T00:00:00Z"/></t:FieldURIOrConstant>
            </t:IsGreaterThanOrEqualTo>`;
      const unreadFilter = `
            <t:IsEqualTo>
                <t:FieldURI FieldURI="message:IsRead"/>
                <t:FieldURIOrConstant><t:Constant Value="false"/></t:FieldURIOrConstant>
            </t:IsEqualTo>`;
      restriction = `<t:And>${dateFilter}${unreadFilter}</t:And>`;
    }

    const doc = await this.makeEwsRequest(this.findItemXml(restriction));
    return parseEwsItems(doc);
  }

  async searchEmails(options: SearchOptions): Promise<EmailSummary[]> {
    const queryParts: string[] = [];
    if (options.query) queryParts.push(options.query);
    if (options.from) queryParts.push(`from:${options.from}`);

    const dateFrom = isIsoDate(options.dateFrom) ? options.dateFrom : undefined;
    const dateTo = isIsoDate(options.dateTo) ? options.dateTo : undefined;

    const dateRestriction =
      dateFrom || dateTo
        ? `
            <t:And>
                ${
                  dateFrom
                    ? `<t:IsGreaterThanOrEqualTo>
                    <t:FieldURI FieldURI="item:DateTimeReceived"/>
                    <t:FieldURIOrConstant><t:Constant Value="${dateFrom}T00:00:00Z"/></t:FieldURIOrConstant>
                </t:IsGreaterThanOrEqualTo>`
                    : ""
                }
                ${
                  dateTo
                    ? `<t:IsLessThanOrEqualTo>
                    <t:FieldURI FieldURI="item:DateTimeReceived"/>
                    <t:FieldURIOrConstant><t:Constant Value="${dateTo}T23:59:59Z"/></t:FieldURIOrConstant>
                </t:IsLessThanOrEqualTo>`
                    : ""
                }
            </t:And>`
        : "";

    const queryString = queryParts.length > 0 ? xmlEscape(queryParts.join(" ")) : undefined;
    const doc = await this.makeEwsRequest(this.findItemXml(dateRestriction, queryString));
    return parseEwsItems(doc);
  }

  async getEmailBody(itemId: string): Promise<string> {
    const xml = ewsEnvelope(`
            <m:GetItem>
                <m:ItemShape>
                    <t:BaseShape>IdOnly</t:BaseShape>
                    <t:AdditionalProperties>
                        <t:FieldURI FieldURI="item:Body"/>
                    </t:AdditionalProperties>
                    <t:BodyType>Text</t:BodyType>
                </m:ItemShape>
                <m:ItemIds>
                    <t:ItemId Id="${xmlEscape(itemId)}"/>
                </m:ItemIds>
            </m:GetItem>`);
    const doc = await this.makeEwsRequest(xml);
    return tag(doc, "Body")?.textContent ?? "";
  }

  async getEmailSummary(itemId: string): Promise<EmailSummary> {
    const xml = ewsEnvelope(`
            <m:GetItem>
                <m:ItemShape>
                    <t:BaseShape>IdOnly</t:BaseShape>
                    <t:AdditionalProperties>
                        <t:FieldURI FieldURI="item:Subject"/>
                        <t:FieldURI FieldURI="message:From"/>
                        <t:FieldURI FieldURI="item:DateTimeReceived"/>
                        <t:FieldURI FieldURI="message:IsRead"/>
                        <t:FieldURI FieldURI="item:Body"/>
                    </t:AdditionalProperties>
                    <t:BodyType>Text</t:BodyType>
                </m:ItemShape>
                <m:ItemIds>
                    <t:ItemId Id="${xmlEscape(itemId)}"/>
                </m:ItemIds>
            </m:GetItem>`);
    const doc = await this.makeEwsRequest(xml);
    const [summary] = parseEwsItems(doc);
    if (!summary) throw new Error("Email not found.");
    return { ...summary, id: itemId };
  }

  async draftNewEmail(to: string, subject: string, body: string): Promise<void> {
    openComposeWindow(to, subject, body);
  }

  async replyToEmail(_itemId: string, body: string): Promise<void> {
    // displayReplyForm is on the current item, not on Mailbox — Office.js reply
    // always targets the currently selected item. _itemId is accepted for
    // interface consistency with GraphAdapter.
    openReplyWindow(body);
  }

  async listCalendarEvents(): Promise<CalendarEvent[]> {
    // EWS has a FindItem/CalendarView equivalent, but it is not implemented in
    // v1 (see plan gap-1) — degrade cleanly instead of a confusing protocol error.
    throw new Error(CALENDAR_REQUIRES_GRAPH_MESSAGE);
  }

  async createEvent(): Promise<string> {
    throw new Error(CALENDAR_WRITE_REQUIRES_GRAPH_MESSAGE);
  }

  async respondToEvent(): Promise<void> {
    throw new Error(CALENDAR_WRITE_REQUIRES_GRAPH_MESSAGE);
  }

  async listFolders(): Promise<MailFolder[]> {
    throw new Error(FOLDERS_REQUIRE_GRAPH_MESSAGE);
  }

  async moveEmail(): Promise<void> {
    throw new Error(MOVE_REQUIRES_GRAPH_MESSAGE);
  }

  async applyCategories(itemId: string, categories: string[]): Promise<void> {
    return applyCategoriesToCurrentItem(itemId, categories);
  }

  async createFolder(): Promise<MailFolder> {
    throw new Error(CREATE_FOLDER_REQUIRES_GRAPH_MESSAGE);
  }

  async deleteFolder(): Promise<void> {
    throw new Error(DELETE_FOLDER_REQUIRES_GRAPH_MESSAGE);
  }

  async deleteEmail(): Promise<void> {
    throw new Error(DELETE_EMAIL_REQUIRES_GRAPH_MESSAGE);
  }

  async listLabels(): Promise<MasterCategory[]> {
    throw new Error(LABELS_REQUIRE_GRAPH_MESSAGE);
  }

  async createLabel(): Promise<MasterCategory> {
    throw new Error(LABELS_REQUIRE_GRAPH_MESSAGE);
  }

  async applyLabel(): Promise<void> {
    throw new Error(LABELS_REQUIRE_GRAPH_MESSAGE);
  }

  async removeLabel(): Promise<void> {
    throw new Error(LABELS_REQUIRE_GRAPH_MESSAGE);
  }

  async listRetentionLabels(): Promise<RetentionLabel[]> {
    throw new Error(RETENTION_REQUIRES_GRAPH_MESSAGE);
  }

  async applyRetentionLabel(): Promise<void> {
    throw new Error(RETENTION_REQUIRES_GRAPH_MESSAGE);
  }

  // Office.js/EWS exposes no sensitivity-label API at all (ADR-005) — always
  // "unknown" to the classification gate, never a thrown error.
  async getSensitivityLabel(): Promise<string | null> {
    return null;
  }

  async createRemoteTask(): Promise<string> {
    throw new Error(TASK_SYNC_REQUIRES_GRAPH_MESSAGE);
  }

  async updateRemoteTask(): Promise<void> {
    throw new Error(TASK_SYNC_REQUIRES_GRAPH_MESSAGE);
  }

  async deleteRemoteTask(): Promise<void> {
    throw new Error(TASK_SYNC_REQUIRES_GRAPH_MESSAGE);
  }
}

// ─── GraphAdapter ─────────────────────────────────────────────────────────────

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

// Dedicated Microsoft To Do list name for synced follow-up tasks — never the
// user's default list, so synced tasks stay clearly attributable to this app.
const TODO_LIST_NAME = "AI Assistant";
const GRAPH_FIELDS = "$select=id,subject,from,receivedDateTime,isRead,body,categories";

// getSensitivityLabel is called once per email in a batch (up to
// NEEDS_REPLY_CAP/50 items, in parallel via Promise.all — see
// data-classification.ts's redactSensitiveSummaries), unlike every other
// Graph read here which is a single, user-initiated call. `fetch()` has no
// default timeout, so on a slow/flaky network a single stuck request would
// hang the whole classification gate (and whatever feature is waiting on
// it, e.g. the day-summary digest) indefinitely. Bound it — a timeout
// degrades to "unknown" exactly like any other read failure (ADR-005),
// never blocks the caller.
const SENSITIVITY_LABEL_TIMEOUT_MS = 5000;

interface GraphMessage {
  id: string;
  subject: string;
  from: { emailAddress: { name: string; address: string } };
  receivedDateTime: string;
  isRead: boolean;
  body: { contentType: string; content: string };
  categories?: string[];
}

interface GraphCalendarEvent {
  id: string;
  subject: string;
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  organizer: { emailAddress: { name: string; address: string } };
  location: { displayName: string };
}

// Shown when an elevated operation (write, calendar) can't get its scope
// silently — the baseline connect token doesn't cover it, and the account
// hasn't consented to the wider scope yet. Actionable, and doesn't leak MSAL
// internals to the user.
const NEEDS_ELEVATION_MESSAGE =
  "This action needs additional mailbox permission. Reconnect to grant it.";

// GUARDRAIL: system/well-known folders the agent must never delete. Addressable
// by these well-known names in Graph (`/me/mailFolders/{name}`), which resolve
// to the mailbox's real folder ids for comparison.
const PROTECTED_WELL_KNOWN_FOLDERS = [
  "inbox",
  "sentitems",
  "drafts",
  "deleteditems",
  "junkemail",
  "archive",
  "outbox",
] as const;

const SYSTEM_FOLDER_PROTECTED_MESSAGE =
  "Refusing to delete a system folder (Inbox, Sent Items, Drafts, Deleted Items, " +
  "Junk Email, Archive, or Outbox).";

export class GraphAdapter implements MailboxAdapter {
  readonly type = "graph" as const;

  // Cache of the mailbox's protected folder ids, resolved lazily once per
  // adapter instance (see getProtectedFolderIds / deleteFolder).
  private protectedFolderIds: Set<string> | null = null;

  // Cache of the dedicated "AI Assistant" To Do list id, resolved lazily once
  // per adapter instance (see ensureToDoListId) — mirrors protectedFolderIds.
  private toDoListId: string | null = null;

  // `token` is the baseline bearer header (`Bearer <access token>`) used for
  // all read operations. `clientId` is retained so elevated operations can
  // acquire their own scope lazily via `ensureGraphToken` — the connect-time
  // token only ever carries `BASELINE_SCOPES` (incremental consent).
  constructor(
    private readonly token: string,
    private readonly clientId: string
  ) {}

  /**
   * Acquires a bearer header for an elevated `scopes` set at call time. MSAL
   * returns a cached/refreshed token silently once the account has consented
   * to `scopes` (e.g. via a prior interactive step or admin consent).
   * Throws {@link GraphConsentRequiredError} — never a raw MSAL error — when
   * the scope hasn't been consented to yet, so callers get an actionable
   * message instead of a crash.
   */
  private async elevatedAuthHeader(scopes: string[]): Promise<string> {
    const token = await ensureGraphToken(this.clientId, scopes);
    if (!token) {
      // Carry the specific scope(s) this operation needed — never the full
      // feature-scope bundle — so a re-consent popup (elevateGraphAdapter)
      // can request just this, instead of dragging in unrelated admin-only
      // scopes (I1: RecordsManagement.ReadWrite.All blocking label/delete
      // elevation for non-admin users).
      throw new GraphConsentRequiredError(NEEDS_ELEVATION_MESSAGE, scopes);
    }
    return `Bearer ${token}`;
  }

  private async graphGet<T>(
    url: string,
    authHeader: string = this.token,
    timeoutMs?: number
  ): Promise<T> {
    const controller = timeoutMs !== undefined ? new AbortController() : undefined;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
    try {
      const response = await fetch(url, {
        headers: { Authorization: authHeader, "Content-Type": "application/json" },
        signal: controller?.signal,
      });
      if (!response.ok) {
        throw new Error(`Graph API error: ${response.statusText}`);
      }
      return (await response.json()) as T;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  private toSummary(msg: GraphMessage): EmailSummary {
    return {
      id: msg.id,
      subject: msg.subject ?? "(no subject)",
      from: msg.from?.emailAddress?.address ?? "",
      receivedAt: msg.receivedDateTime,
      isRead: msg.isRead,
      preview: (msg.body?.content ?? "").slice(0, 200),
      categories: msg.categories ?? [],
    };
  }

  async listEmails(filter: ListFilter): Promise<EmailSummary[]> {
    const today = new Date().toISOString().split("T")[0];
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

    let filterParam = "";
    if (filter === "today") {
      filterParam = `receivedDateTime ge ${today}T00:00:00Z`;
    } else if (filter === "unread") {
      filterParam = "isRead eq false";
    } else if (filter === "needs_reply") {
      filterParam = `isRead eq false and receivedDateTime ge ${sevenDaysAgo}T00:00:00Z`;
    }
    // filter === "recent" → no $filter clause (most-recent inbox slice)

    const filterClause = filterParam ? `&$filter=${encodeURIComponent(filterParam)}` : "";
    const url =
      `${GRAPH_BASE}/me/messages?${GRAPH_FIELDS}` +
      filterClause +
      `&$orderby=${encodeURIComponent("receivedDateTime desc")}` +
      `&$top=50`;

    const data = await this.graphGet<{ value: GraphMessage[] }>(url);
    return data.value.map((m) => this.toSummary(m));
  }

  async searchEmails(options: SearchOptions): Promise<EmailSummary[]> {
    const params = new URLSearchParams();
    params.set("$select", "id,subject,from,receivedDateTime,isRead,body");
    params.set("$top", "50");

    if (options.query) {
      // Graph /me/messages rejects $search combined with $orderby or $filter
      // (400 InvalidRequest). Use a KQL $search phrase only, folding the
      // sender and date bounds into it via KQL property restrictions
      // (`from:`, `received>=`/`received<=`). Graph orders search results by
      // relevance/date automatically.
      const terms = [options.query];
      if (options.from) terms.push(`from:${options.from}`);
      if (isIsoDate(options.dateFrom)) terms.push(`received>=${options.dateFrom}`);
      if (isIsoDate(options.dateTo)) terms.push(`received<=${options.dateTo}`);
      params.set("$search", `"${escapeODataQuoted(terms.join(" "))}"`);
    } else {
      params.set("$orderby", "receivedDateTime desc");
      const filters: string[] = [];
      if (options.from)
        filters.push(`from/emailAddress/address eq '${escapeODataLiteral(options.from)}'`);
      if (isIsoDate(options.dateFrom))
        filters.push(`receivedDateTime ge ${options.dateFrom}T00:00:00Z`);
      if (isIsoDate(options.dateTo))
        filters.push(`receivedDateTime le ${options.dateTo}T23:59:59Z`);
      if (filters.length > 0) params.set("$filter", filters.join(" and "));
    }

    const url = `${GRAPH_BASE}/me/messages?${params.toString()}`;
    const data = await this.graphGet<{ value: GraphMessage[] }>(url);
    return data.value.map((m) => this.toSummary(m));
  }

  async getEmailBody(itemId: string): Promise<string> {
    const url = `${GRAPH_BASE}/me/messages/${encodeURIComponent(itemId)}?$select=body`;
    const msg = await this.graphGet<{ body: { content: string } }>(url);
    return msg.body?.content ?? "";
  }

  async getEmailSummary(itemId: string): Promise<EmailSummary> {
    const url = `${GRAPH_BASE}/me/messages/${encodeURIComponent(itemId)}?${GRAPH_FIELDS}`;
    const msg = await this.graphGet<GraphMessage>(url);
    return this.toSummary(msg);
  }

  async draftNewEmail(to: string, subject: string, body: string): Promise<void> {
    openComposeWindow(to, subject, body);
  }

  async replyToEmail(_itemId: string, body: string): Promise<void> {
    openReplyWindow(body);
  }

  async listCalendarEvents(fromIso: string, toIso: string): Promise<CalendarEvent[]> {
    const params = new URLSearchParams({
      startDateTime: fromIso,
      endDateTime: toIso,
      $select: "subject,start,end,organizer,location",
      $orderby: "start/dateTime",
      $top: "50",
    });
    const url = `${GRAPH_BASE}/me/calendarView?${params.toString()}`;
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.calendarsRead);
    const data = await this.graphGet<{ value: GraphCalendarEvent[] }>(url, authHeader);
    return data.value.map((e) => this.toCalendarEvent(e));
  }

  private toCalendarEvent(event: GraphCalendarEvent): CalendarEvent {
    return {
      id: event.id,
      subject: event.subject ?? "(no subject)",
      start: event.start?.dateTime ?? "",
      end: event.end?.dateTime ?? "",
      organizer: event.organizer?.emailAddress?.address ?? "",
      location: event.location?.displayName ?? "",
    };
  }

  // Builds the {dateTime, timeZone} shape Graph expects on an event's start/end
  // (and, elsewhere, a To Do task's dueDateTime): dateTime is LOCAL wall-clock
  // time in `timeZone`, never UTC, so any trailing "Z"/offset on the caller's
  // ISO string is stripped first — keeping both an offset and an explicit IANA
  // zone would be a contradiction Graph resolves unpredictably.
  private toGraphEventDateTime(iso: string): { dateTime: string; timeZone: string } {
    return {
      dateTime: iso.replace(/(Z|[+-]\d{2}:\d{2})$/, ""),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  // NON-destructive — creates a new event on the signed-in user's calendar.
  // Never requires confirmation (agent-loop.ts): creating your own event is
  // easily undone (delete/edit in Outlook).
  async createEvent(event: {
    subject: string;
    start: string;
    end: string;
    attendees?: string[];
    body?: string;
  }): Promise<string> {
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.calendarsReadWrite);
    const payload: Record<string, unknown> = {
      subject: event.subject,
      start: this.toGraphEventDateTime(event.start),
      end: this.toGraphEventDateTime(event.end),
    };
    if (event.attendees && event.attendees.length > 0) {
      payload.attendees = event.attendees.map((address) => ({
        emailAddress: { address },
        type: "required",
      }));
    }
    if (event.body) {
      payload.body = { contentType: "text", content: event.body };
    }
    const created = await this.graphPostJson<{ id: string }>(
      `${GRAPH_BASE}/me/events`,
      payload,
      authHeader
    );
    return created.id;
  }

  // Responds to a meeting invite (accept/decline/tentativelyAccept). Callers
  // (agent-loop.ts) must confirm before invoking this — unlike createEvent, a
  // meeting response is a one-way signal visible to the organizer.
  async respondToEvent(
    eventId: string,
    response: "accept" | "decline" | "tentativelyAccept"
  ): Promise<void> {
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.calendarsReadWrite);
    const url = `${GRAPH_BASE}/me/events/${encodeURIComponent(eventId)}/${response}`;
    await this.graphWrite(url, "POST", undefined, authHeader);
  }

  async listFolders(): Promise<MailFolder[]> {
    const url = `${GRAPH_BASE}/me/mailFolders?$top=100`;
    const data = await this.graphGet<{ value: Array<{ id: string; displayName: string }> }>(url);
    return data.value.map((f) => ({ id: f.id, displayName: f.displayName }));
  }

  private async graphWrite(
    url: string,
    method: "POST" | "PATCH" | "DELETE",
    body: unknown | undefined,
    authHeader: string
  ): Promise<void> {
    const response = await fetch(url, {
      method,
      headers: { Authorization: authHeader, "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(`Graph API error: ${response.statusText}`);
    }
  }

  private async graphPostJson<T>(url: string, body: unknown, authHeader: string): Promise<T> {
    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: authHeader, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(`Graph API error: ${response.statusText}`);
    }
    return (await response.json()) as T;
  }

  async moveEmail(itemId: string, folderId: string): Promise<void> {
    const url = `${GRAPH_BASE}/me/messages/${encodeURIComponent(itemId)}/move`;
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.mailReadWrite);
    await this.graphWrite(url, "POST", { destinationId: folderId }, authHeader);
  }

  async applyCategories(itemId: string, categories: string[]): Promise<void> {
    const url = `${GRAPH_BASE}/me/messages/${encodeURIComponent(itemId)}`;
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.mailReadWrite);
    await this.graphWrite(url, "PATCH", { categories }, authHeader);
  }

  // NON-destructive — creates a new mailbox folder, at the root or nested
  // under `parentFolderId`. Never requires user confirmation (agent-loop.ts).
  async createFolder(name: string, parentFolderId?: string): Promise<MailFolder> {
    const url = parentFolderId
      ? `${GRAPH_BASE}/me/mailFolders/${encodeURIComponent(parentFolderId)}/childFolders`
      : `${GRAPH_BASE}/me/mailFolders`;
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.mailReadWrite);
    const data = await this.graphPostJson<{ id: string; displayName: string }>(
      url,
      { displayName: name },
      authHeader
    );
    return { id: data.id, displayName: data.displayName };
  }

  /**
   * Resolves (and caches) the ids of the mailbox's protected well-known folders
   * so {@link deleteFolder} can refuse to delete them. A well-known folder that
   * doesn't exist for this mailbox (e.g. `archive`) is skipped, not fatal.
   */
  private async getProtectedFolderIds(): Promise<Set<string>> {
    if (this.protectedFolderIds) {
      return this.protectedFolderIds;
    }
    const ids = new Set<string>();
    await Promise.all(
      PROTECTED_WELL_KNOWN_FOLDERS.map(async (name) => {
        try {
          const data = await this.graphGet<{ id?: string }>(
            `${GRAPH_BASE}/me/mailFolders/${name}?$select=id`
          );
          if (data?.id) ids.add(data.id);
        } catch {
          // Folder not present for this mailbox — nothing to protect.
        }
      })
    );
    this.protectedFolderIds = ids;
    return ids;
  }

  // DESTRUCTIVE (soft delete) — moves the folder to Deleted Items, recoverable
  // by the user. Callers (agent-loop.ts) must confirm before invoking this.
  // GUARDRAIL: never deletes a system/well-known folder (Inbox, Sent Items, …).
  async deleteFolder(folderId: string): Promise<void> {
    // Resolve elevation (and its consent gate) before any fetch, so an
    // un-consented scope fails cleanly without touching the mailbox.
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.mailReadWrite);
    const protectedIds = await this.getProtectedFolderIds();
    if (protectedIds.has(folderId)) {
      throw new Error(SYSTEM_FOLDER_PROTECTED_MESSAGE);
    }
    const url = `${GRAPH_BASE}/me/mailFolders/${encodeURIComponent(folderId)}`;
    await this.graphWrite(url, "DELETE", undefined, authHeader);
  }

  // GUARDRAIL: "deleting" an email MOVES it to Deleted Items — never a hard
  // delete. An explicit move to the well-known `deleteditems` folder is always
  // recoverable and, unlike `DELETE /messages/{id}` (which further soft-deletes
  // a message that is already in Deleted Items into Recoverable Items), never
  // approaches the irreversible `permanentDelete` action. Callers (agent-loop.ts)
  // still confirm before invoking this.
  async deleteEmail(messageId: string): Promise<void> {
    const url = `${GRAPH_BASE}/me/messages/${encodeURIComponent(messageId)}/move`;
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.mailReadWrite);
    await this.graphWrite(url, "POST", { destinationId: "deleteditems" }, authHeader);
  }

  // ── Labels (Outlook categories via masterCategories) ──────────────────────
  // GET/POST /me/outlook/masterCategories needs MailboxSettings.ReadWrite.
  // apply/remove read+PATCH the message's `categories` array (Mail.ReadWrite).
  // All non-destructive — never require confirmation (agent-loop.ts).

  async listLabels(): Promise<MasterCategory[]> {
    const url = `${GRAPH_BASE}/me/outlook/masterCategories`;
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.mailboxSettingsReadWrite);
    const data = await this.graphGet<{ value: MasterCategory[] }>(url, authHeader);
    return data.value.map((c) => ({ id: c.id, displayName: c.displayName, color: c.color }));
  }

  async createLabel(name: string, color: string = "preset0"): Promise<MasterCategory> {
    const url = `${GRAPH_BASE}/me/outlook/masterCategories`;
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.mailboxSettingsReadWrite);
    const data = await this.graphPostJson<MasterCategory>(
      url,
      { displayName: name, color },
      authHeader
    );
    return { id: data.id, displayName: data.displayName, color: data.color };
  }

  // Reads the message's current categories so apply/remove are additive/subtractive
  // rather than overwriting the whole array (which apply_categories does).
  private async readMessageCategories(messageId: string, authHeader: string): Promise<string[]> {
    const url = `${GRAPH_BASE}/me/messages/${encodeURIComponent(messageId)}?$select=categories`;
    const msg = await this.graphGet<{ categories?: string[] }>(url, authHeader);
    return msg.categories ?? [];
  }

  async applyLabel(messageId: string, names: string[]): Promise<void> {
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.mailReadWrite);
    const current = await this.readMessageCategories(messageId, authHeader);
    const merged = Array.from(new Set([...current, ...names]));
    const url = `${GRAPH_BASE}/me/messages/${encodeURIComponent(messageId)}`;
    await this.graphWrite(url, "PATCH", { categories: merged }, authHeader);
  }

  async removeLabel(messageId: string, names: string[]): Promise<void> {
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.mailReadWrite);
    const current = await this.readMessageCategories(messageId, authHeader);
    const toRemove = new Set(names);
    const remaining = current.filter((c) => !toRemove.has(c));
    const url = `${GRAPH_BASE}/me/messages/${encodeURIComponent(messageId)}`;
    await this.graphWrite(url, "PATCH", { categories: remaining }, authHeader);
  }

  // ── Microsoft Purview retention labels ────────────────────────────────────
  //
  // RESEARCH FINDINGS (Microsoft Learn, verified 2026-07):
  //
  // • listRetentionLabels — `GET /security/labels/retentionLabels`
  //   (namespace microsoft.graph.security). Delegated permission:
  //   RecordsManagement.Read.All (least) or RecordsManagement.ReadWrite.All.
  //   Returns the org's retention label DEFINITIONS. This is real and works
  //   when the scope is consented.
  //
  // • applyRetentionLabel — THERE IS NO first-class delegated Graph endpoint to
  //   assign a Purview retention label to an individual Outlook mail item. The
  //   `driveItem: setRetentionLabel` action exists ONLY for SharePoint/OneDrive
  //   files & folders (driveItem), not for `message`. In the Outlook UI this is
  //   the "Assign Policy" command, which is backed at the MAPI layer by the
  //   PolicyTag/ComplianceTag property (the retention label GUID). The only
  //   technical route from Graph is writing that MAPI property via the message's
  //   singleValueExtendedProperties (PolicyTag = Binary 0x3019, value = the label
  //   GUID as a .NET byte array). Exchange Online commonly REJECTS client-written
  //   policy/compliance tags (server-controlled), so this is best-effort and is
  //   expected to fail (400/403) in most tenants — we translate every failure
  //   into a clear, non-crashing "not available" message.

  async listRetentionLabels(): Promise<RetentionLabel[]> {
    // Read-only — least privilege (CLAUDE.md A5): only Read.All, never the
    // read-write scope (M2).
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.recordsManagementReadAll);
    try {
      const url = `${GRAPH_BASE}/security/labels/retentionLabels`;
      const data = await this.graphGet<{ value: RetentionLabel[] }>(url, authHeader);
      return data.value.map((l) => ({ id: l.id, displayName: l.displayName }));
    } catch (error) {
      // 403/404/not-supported → surface a clear, actionable message rather than
      // a raw protocol error. Consent errors are thrown before this try block.
      throw retentionUnavailable(error);
    }
  }

  async applyRetentionLabel(messageId: string, label: string): Promise<void> {
    // The write is a PATCH of the message's singleValueExtendedProperties, which
    // needs Mail.ReadWrite in addition to the records-management scope (M1) —
    // acquiring only RecordsManagement.ReadWrite.All always 403s the PATCH even
    // in a tenant that would otherwise accept the PolicyTag write.
    const authHeader = await this.elevatedAuthHeader([
      ...FEATURE_SCOPES.recordsManagementReadWriteAll,
      ...FEATURE_SCOPES.mailReadWrite,
    ]);
    const url = `${GRAPH_BASE}/me/messages/${encodeURIComponent(messageId)}`;
    try {
      // guidToPolicyTagBase64 (validates `label` is a GUID) moved inside the
      // try (M4): a non-GUID label id must degrade gracefully through
      // retentionUnavailable like every other failure path here, not throw a
      // raw validation error that bypasses it.
      const body = {
        singleValueExtendedProperties: [
          { id: "Binary 0x3019", value: guidToPolicyTagBase64(label) },
        ],
      };
      await this.graphWrite(url, "PATCH", body, authHeader);
    } catch (error) {
      throw retentionUnavailable(error);
    }
  }

  // ── Microsoft Purview sensitivity labels (MIP/AIP) ────────────────────────
  //
  // Graph has no first-class "sensitivityLabel" property on `message` (that
  // exists only for driveItem/SharePoint). When a sensitivity label is applied
  // in Outlook, the MIP add-in stamps the transport header `msip_labels` with
  // `MSIP_Label_<GUID>_Name=<display name>` (plus Enabled/SetDate/Method/etc.
  // siblings) — reading it via `internetMessageHeaders` stays within the
  // baseline `Mail.Read` scope, no elevated consent needed. The exact header
  // format can vary slightly by tenant MIP configuration; treat any
  // parse/read failure as "no label" (`null`), never a thrown error — the
  // classification gate (data-classification.ts) treats that as "unknown"
  // and falls back to the content heuristic (ADR-005).

  private parseMsipLabelName(
    headers: Array<{ name: string; value: string }> | undefined
  ): string | null {
    const header = headers?.find((h) => h.name.toLowerCase() === "msip_labels");
    if (!header) return null;
    const match = /MSIP_Label_[0-9a-fA-F-]+_Name=([^;]+)/.exec(header.value);
    return match ? match[1].trim() : null;
  }

  async getSensitivityLabel(messageId: string): Promise<string | null> {
    try {
      const url = `${GRAPH_BASE}/me/messages/${encodeURIComponent(messageId)}?$select=internetMessageHeaders`;
      const msg = await this.graphGet<{
        internetMessageHeaders?: Array<{ name: string; value: string }>;
      }>(url, this.token, SENSITIVITY_LABEL_TIMEOUT_MS);
      return this.parseMsipLabelName(msg.internetMessageHeaders);
    } catch {
      return null;
    }
  }

  // ── Microsoft To Do sync (one-way: add-in → To Do) ────────────────────────
  //
  // RESEARCH FINDINGS (Microsoft Learn, verified 2026-07):
  //
  // • `GET /me/todo/lists` / `POST /me/todo/lists` (`{displayName}`) — every
  //   Graph operation on a task requires the owning list's id in the path;
  //   there is no `/me/todo/tasks/{id}` shortcut. The list is resolved once
  //   per adapter instance and cached (`toDoListId`), same pattern as
  //   `protectedFolderIds`.
  // • `POST /me/todo/lists/{listId}/tasks` (`{title, status, dueDateTime}`) to
  //   create; `PATCH` the same path + `/{taskId}` to update; `DELETE` to
  //   remove. `status` is `"notStarted"` or `"completed"` (a subset of the
  //   full enum — this app never sets the others). `dueDateTime` is a
  //   `{dateTime, timeZone}` pair; passing `null` clears an existing due date
  //   (omitting the field leaves whatever Graph already has).

  private buildDueDateTime(dueDate: string): { dateTime: string; timeZone: string } {
    return {
      dateTime: `${dueDate}T00:00:00`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  private async ensureToDoListId(authHeader: string): Promise<string> {
    if (this.toDoListId) {
      return this.toDoListId;
    }
    const data = await this.graphGet<{ value: Array<{ id: string; displayName: string }> }>(
      `${GRAPH_BASE}/me/todo/lists`,
      authHeader
    );
    const existing = data.value.find((l) => l.displayName === TODO_LIST_NAME);
    const listId = existing
      ? existing.id
      : (
          await this.graphPostJson<{ id: string }>(
            `${GRAPH_BASE}/me/todo/lists`,
            { displayName: TODO_LIST_NAME },
            authHeader
          )
        ).id;
    this.toDoListId = listId;
    return listId;
  }

  async createRemoteTask(title: string, dueDate?: string): Promise<string> {
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.tasksReadWrite);
    const listId = await this.ensureToDoListId(authHeader);
    const body: Record<string, unknown> = { title, status: "notStarted" };
    if (dueDate) {
      body.dueDateTime = this.buildDueDateTime(dueDate);
    }
    const created = await this.graphPostJson<{ id: string }>(
      `${GRAPH_BASE}/me/todo/lists/${encodeURIComponent(listId)}/tasks`,
      body,
      authHeader
    );
    return created.id;
  }

  async updateRemoteTask(
    taskId: string,
    task: { title: string; dueDate?: string; done: boolean }
  ): Promise<void> {
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.tasksReadWrite);
    const listId = await this.ensureToDoListId(authHeader);
    const body: Record<string, unknown> = {
      title: task.title,
      status: task.done ? "completed" : "notStarted",
      dueDateTime: task.dueDate ? this.buildDueDateTime(task.dueDate) : null,
    };
    await this.graphWrite(
      `${GRAPH_BASE}/me/todo/lists/${encodeURIComponent(listId)}/tasks/${encodeURIComponent(taskId)}`,
      "PATCH",
      body,
      authHeader
    );
  }

  async deleteRemoteTask(taskId: string): Promise<void> {
    const authHeader = await this.elevatedAuthHeader(FEATURE_SCOPES.tasksReadWrite);
    const listId = await this.ensureToDoListId(authHeader);
    await this.graphWrite(
      `${GRAPH_BASE}/me/todo/lists/${encodeURIComponent(listId)}/tasks/${encodeURIComponent(taskId)}`,
      "DELETE",
      undefined,
      authHeader
    );
  }
}

/**
 * Converts a retention label GUID to the base64 of its .NET `Guid.ToByteArray()`
 * byte layout (first three groups little-endian, final eight bytes big-endian),
 * as required for the MAPI PolicyTag binary extended property. Throws when the
 * input isn't a GUID so a blank/garbage id never reaches Graph.
 */
export function guidToPolicyTagBase64(guid: string): string {
  const hex = guid.replace(/[{}-]/g, "");
  if (!/^[0-9a-fA-F]{32}$/.test(hex)) {
    throw new Error("Retention label id must be a GUID.");
  }
  const b = (hex.match(/.{2}/g) as string[]).map((h) => parseInt(h, 16));
  const bytes = [
    b[3],
    b[2],
    b[1],
    b[0], // data1 (LE)
    b[5],
    b[4], // data2 (LE)
    b[7],
    b[6], // data3 (LE)
    b[8],
    b[9],
    b[10],
    b[11],
    b[12],
    b[13],
    b[14],
    b[15], // data4 (BE)
  ];
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

// ─── UnavailableMailboxAdapter ────────────────────────────────────────────────
// Graceful degradation: a Graph client ID is configured but no token could be
// acquired (consent declined, sign-in cancelled). Mailbox-wide reads return a
// clear "connect your account" message; compose actions still work via Office.js.

export class UnavailableMailboxAdapter implements MailboxAdapter {
  readonly type = "unavailable" as const;

  async listEmails(): Promise<EmailSummary[]> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async searchEmails(): Promise<EmailSummary[]> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async getEmailBody(): Promise<string> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async getEmailSummary(): Promise<EmailSummary> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async draftNewEmail(to: string, subject: string, body: string): Promise<void> {
    openComposeWindow(to, subject, body);
  }

  async replyToEmail(_itemId: string, body: string): Promise<void> {
    openReplyWindow(body);
  }

  async listCalendarEvents(): Promise<CalendarEvent[]> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async createEvent(): Promise<string> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async respondToEvent(): Promise<void> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async listFolders(): Promise<MailFolder[]> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async moveEmail(): Promise<void> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async applyCategories(itemId: string, categories: string[]): Promise<void> {
    return applyCategoriesToCurrentItem(itemId, categories);
  }

  async createFolder(): Promise<MailFolder> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async deleteFolder(): Promise<void> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async deleteEmail(): Promise<void> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async listLabels(): Promise<MasterCategory[]> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async createLabel(): Promise<MasterCategory> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async applyLabel(): Promise<void> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async removeLabel(): Promise<void> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async listRetentionLabels(): Promise<RetentionLabel[]> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async applyRetentionLabel(): Promise<void> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async getSensitivityLabel(): Promise<string | null> {
    return null;
  }

  async createRemoteTask(): Promise<string> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async updateRemoteTask(): Promise<void> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }

  async deleteRemoteTask(): Promise<void> {
    throw new Error(CONNECT_ACCOUNT_MESSAGE);
  }
}

// ─── Capability detection ─────────────────────────────────────────────────────

/**
 * Selects the mailbox adapter on page load. The client ID resolves from the
 * Settings override first, then the hardcoded {@link GRAPH_CLIENT_ID} default.
 * Only silent (non-interactive) token acquisition is attempted here — this
 * must never pop up a sign-in window on load:
 *  - No client ID, or the host has no NAA support (on-premises Exchange) →
 *    OfficeJsAdapter (EWS).
 *  - A client ID resolves, NAA is supported, and a cached token is acquired
 *    silently → GraphAdapter (Exchange Online).
 *  - A client ID resolves and NAA is supported but no cached token →
 *    UnavailableMailboxAdapter (graceful degrade; user must connect via
 *    {@link connectGraphAdapter} from a user gesture).
 */
export async function detectAdapter(settings: AddinSettings): Promise<MailboxAdapter> {
  const clientId = settings.graphClientId || GRAPH_CLIENT_ID;
  // No Graph app configured, or the host has no NAA (on-premises Exchange):
  // use EWS via Office.js. Never trigger interactive auth here (page load).
  if (!clientId || !isNaaSupported()) {
    debugLog("adapter", "officejs (ews)");
    return new OfficeJsAdapter();
  }
  const token = await getGraphTokenSilent(clientId);
  if (token) {
    debugLog("adapter", "graph");
    return new GraphAdapter(`Bearer ${token}`, clientId);
  }
  debugLog("adapter", "unavailable (not signed in)");
  return new UnavailableMailboxAdapter();
}

/**
 * Interactive sign-in for Graph. Call ONLY from a user-gesture handler.
 * Returns a GraphAdapter on success, else UnavailableMailboxAdapter.
 *
 * Only requests {@link BASELINE_SCOPES} (Mail.Read, User.Read) — this is the
 * incremental-consent connect flow: basic connect succeeds even when a
 * tenant/app registration hasn't granted the elevated scopes (Mail.ReadWrite,
 * Calendars.Read) that triage-move/categories and calendar grounding need.
 * `GraphAdapter` acquires those elevated scopes lazily, at the point of use.
 */
export async function connectGraphAdapter(settings: AddinSettings): Promise<MailboxAdapter> {
  const clientId = settings.graphClientId || GRAPH_CLIENT_ID;
  const token = await getGraphTokenInteractive(clientId);
  if (token) {
    debugLog("adapter", "graph (connected)");
    return new GraphAdapter(`Bearer ${token}`, clientId);
  }
  debugLog("adapter", "unavailable (connect failed)");
  return new UnavailableMailboxAdapter();
}

/**
 * Interactive elevation for Graph. Call ONLY from a user-gesture handler (e.g.
 * a "Grant permission" button). `scopes` MUST be just the specific scope(s)
 * the failed operation needed — carried on the {@link GraphConsentRequiredError}
 * that triggered the re-consent affordance (see `ChatPanel.handleGrantConsent`)
 * — never the full feature-scope registry. Bundling every feature scope into
 * one popup used to mean a non-admin user elevating for e.g. `apply_label`
 * (`MailboxSettings.ReadWrite`, user-consentable) would also be asked for
 * `RecordsManagement.ReadWrite.All` (admin-consent-only in most tenants);
 * Azure AD then shows "Need admin approval" and the whole popup fails, even
 * though the scope the user actually needed was consentable on its own (I1).
 * Returns a fresh GraphAdapter on success, else UnavailableMailboxAdapter —
 * never throws.
 */
export async function elevateGraphAdapter(
  settings: AddinSettings,
  scopes: string[]
): Promise<MailboxAdapter> {
  const clientId = settings.graphClientId || GRAPH_CLIENT_ID;
  const requestedScopes = Array.from(new Set([...BASELINE_SCOPES, ...scopes]));
  const token = await getGraphTokenInteractive(clientId, requestedScopes);
  if (token) {
    debugLog("adapter", "graph (elevated)");
    return new GraphAdapter(`Bearer ${token}`, clientId);
  }
  debugLog("adapter", "unavailable (elevation failed)");
  return new UnavailableMailboxAdapter();
}

/**
 * A user-initiated reconnection intent, mapped here (inside the boundary) to
 * the MSAL prompt + scope set it needs, so React components pass a semantic
 * choice and never touch FEATURE_SCOPES/prompt strings directly (CLAUDE.md B3):
 *  - "switch_account"     → account picker, baseline scopes only.
 *  - "expand_permissions" → re-consent screen requesting Mail.ReadWrite.
 */
export type ReconnectIntent = "switch_account" | "expand_permissions";

/**
 * Interactive Graph reconnection for an already-connected (or limited) account.
 * Call ONLY from a user-gesture handler. Returns a fresh GraphAdapter on
 * success, else UnavailableMailboxAdapter — never throws.
 */
export async function reconnectGraphAdapter(
  settings: AddinSettings,
  intent: ReconnectIntent
): Promise<MailboxAdapter> {
  const clientId = settings.graphClientId || GRAPH_CLIENT_ID;
  const prompt = intent === "expand_permissions" ? "consent" : "select_account";
  // "Grant more permissions" pre-consents the user-consentable mailbox write
  // features in one popup: Mail.ReadWrite (move/categorize/delete + apply/remove
  // label) and MailboxSettings.ReadWrite (list/create labels via
  // masterCategories). Deliberately excludes RecordsManagement.* — those are
  // admin-consent-only in most tenants, and bundling an admin-only scope makes
  // Azure block the whole popup for a non-admin user (see elevateGraphAdapter,
  // I1). Retention labels keep their own per-operation incremental consent.
  const scopes =
    intent === "expand_permissions"
      ? Array.from(
          new Set([
            ...BASELINE_SCOPES,
            ...FEATURE_SCOPES.mailReadWrite,
            ...FEATURE_SCOPES.mailboxSettingsReadWrite,
          ])
        )
      : BASELINE_SCOPES;
  const token = await getGraphTokenInteractive(clientId, scopes, prompt);
  if (token) {
    debugLog("adapter", `graph (reconnected: ${intent})`);
    return new GraphAdapter(`Bearer ${token}`, clientId);
  }
  debugLog("adapter", "unavailable (reconnect failed)");
  return new UnavailableMailboxAdapter();
}

/**
 * Best-effort display name (email) of the connected Graph account, or null.
 * Wraps {@link getActiveGraphAccountName} so components stay off the auth layer.
 */
export async function getConnectedAccountName(settings: AddinSettings): Promise<string | null> {
  const clientId = settings.graphClientId || GRAPH_CLIENT_ID;
  return getActiveGraphAccountName(clientId);
}

/**
 * Best-effort local disconnect: clears the MSAL cache (where supported) and
 * returns the Unavailable adapter so the app behaves as signed-out. Under NAA
 * the host brokers SSO, so a subsequent connect may re-acquire silently — this
 * is a local sign-out, not a full Entra revocation. Never throws.
 */
export async function disconnectGraphAdapter(settings: AddinSettings): Promise<MailboxAdapter> {
  const clientId = settings.graphClientId || GRAPH_CLIENT_ID;
  await disconnectGraph(clientId);
  debugLog("adapter", "unavailable (disconnected)");
  return new UnavailableMailboxAdapter();
}

/**
 * True when `error` signals that an elevated Graph scope hasn't been consented
 * to yet ({@link GraphConsentRequiredError}). Exposed here so UI components can
 * offer a re-consent affordance without importing the auth/MSAL layer directly.
 */
export function isGraphConsentError(error: unknown): boolean {
  return error instanceof GraphConsentRequiredError;
}

/**
 * The specific Graph scope(s) a {@link GraphConsentRequiredError} carries, so
 * `ChatPanel` can re-consent for exactly what the failed tool needed (I1)
 * without importing `office-auth`/MSAL directly (boundary-module discipline —
 * CLAUDE.md B3). Returns an empty array for any other error.
 */
export function getConsentScopes(error: unknown): string[] {
  return error instanceof GraphConsentRequiredError ? error.scopes : [];
}
